"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useRestrictedAnalyticsReporting = exports.useAnalyticsReporting = exports.createAnalyticsWrappedCallback = void 0;
var _react = require("react");
var _analytics = _interopRequireDefault(require("../../../packages/analytics"));
const toEnumerableError = function (err) {
  return JSON.parse(JSON.stringify(err, Object.getOwnPropertyNames(err)));
};
/**
 * Creates a wrapped version of the given callback (synchronous or asynchronous) that
 * fires analytics events upon success or error.
 *
 * @typeParam T - The type of the callback function (e.g. `(...args: any[]) => any`).
 *   If `T` is synchronous, its return value is automatically wrapped in a Promise.
 *   If `T` is already an async function, it remains asynchronous.
 * @param callback - The original callback to wrap (may be sync or async).
 * @param fireAnalytics - The analytics function returned by `useAnalytics`.
 * @param successName - The name of the success analytics event.
 * @param errorName - The name of the error analytics event.
 * @returns A wrapped async function that fires the appropriate analytics events
 *   and returns a `Promise`.
 */
const createAnalyticsWrappedCallback = function (callback, fireAnalytics, successName, errorName) {
  return async function () {
    try {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      const response = await Promise.resolve(callback(...args));
      fireAnalytics(successName, {
        payload: args,
        response
      });
      return response;
    } catch (err) {
      fireAnalytics(errorName, toEnumerableError(err));
      throw err;
    }
  };
};

/**
 * Creates a wrapped, restricted version of a callback that fires analytics events
 * without logging potentially sensitive data.
 *
 * @typeParam T - The type of the callback function.
 * @param callback - The original callback to wrap.
 * @param fireAnalytics - The analytics function.
 * @param successName - The name of the success analytics event.
 * @param errorName - The name of the error analytics event.
 * @returns A wrapped async function that fires analytics events without payload,
 *   response, or error details.
 */
exports.createAnalyticsWrappedCallback = createAnalyticsWrappedCallback;
const createRestrictedAnalyticsWrappedCallback = function (callback, fireAnalytics, successName, errorName) {
  return async function () {
    try {
      const response = await Promise.resolve(callback(...arguments));
      fireAnalytics(successName);
      return response;
    } catch (err) {
      fireAnalytics(errorName);
      throw err;
    }
  };
};

/**
 * A React hook that returns a wrapped version of the given callback (sync or async).
 * When invoked, it fires analytics events upon success or error.
 *
 * @typeParam T - The type of the callback function (e.g. `(...args: any[]) => any`).
 *   If `T` is synchronous, its return value will be wrapped in a `Promise`.
 * @param callback - A function (sync or async) whose calls should trigger analytics events.
 * @param options - Optional configuration for analytics:
 *   - `name` is the identifier passed to `useAnalytics`.
 *   - `successName` is the analytics event name for successful calls.
 *   - `errorName` is the analytics event name for error calls.
 * @returns A memoized function that always returns a `Promise<Awaited<ReturnType<T>>>`.
 *   Regardless of whether the original function is sync or async, you must `await` or
 *   handle the returned promise.
 */
const useAnalyticsReporting = function (callback) {
  let {
    name,
    successName = "success",
    errorName = "error"
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const fireAnalytics = (0, _analytics.default)(name);
  return (0, _react.useMemo)(function () {
    return createAnalyticsWrappedCallback(callback, fireAnalytics, successName, errorName);
  }, [callback, fireAnalytics, successName, errorName]);
};

/**
 * A React hook that returns a wrapped version of the given callback (sync or async)
 * that does NOT log function arguments, return values, or error details to analytics,
 * preventing PII leaks.
 *
 * @typeParam T - The type of the callback function (e.g. `(...args: any[]) => any`).
 *   If `T` is synchronous, its return value will be wrapped in a `Promise`.
 * @param callback - A function (sync or async) whose calls should trigger analytics events.
 * @param options - Optional configuration for analytics:
 *   - `name` is the identifier passed to `useAnalytics`.
 *   - `successName` is the analytics event name for successful calls.
 *   - `errorName` is the analytics event name for error calls.
 * @returns A memoized, privacy-safe function that always returns a `Promise<Awaited<ReturnType<T>>>`.
 *   You must `await` or handle the returned promise.
 */
exports.useAnalyticsReporting = useAnalyticsReporting;
const useRestrictedAnalyticsReporting = function (callback) {
  let {
    name,
    successName = "success",
    errorName = "error"
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const fireAnalytics = (0, _analytics.default)(name);
  return (0, _react.useMemo)(function () {
    return createRestrictedAnalyticsWrappedCallback(callback, fireAnalytics, successName, errorName);
  }, [callback, fireAnalytics, successName, errorName]);
};
exports.useRestrictedAnalyticsReporting = useRestrictedAnalyticsReporting;